import {
  pdf,
  Document,
  Page,
  Text as PDFText,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import JsBarcode from "jsbarcode";

export interface OwnDeliveryLabelData {
  deliveryId: string;
  bookingId: number;
  /** e.g. EFF6680AIR / EFF6680SEA */
  bookingReference: string;
  palletIndex: number;
  palletTotal: number;
  toName: string;
  toCompanyName?: string | null;
  toStreet: string;
  toHouseNumber: string;
  toPostalCode: string;
  toCity: string;
  toCountry: string;
  weightGrams?: number | null;
  /** Special delivery instructions from General Information / booking_delivery */
  instructions?: string | null;
}

/** Standard 4×6" thermal shipping label (Dymo 4XL / Zebra) — portrait like carrier labels. */
const DYMO_4X6_PORTRAIT_PT: [number, number] = [4 * 72, 6 * 72];

const hairline = 1;

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica",
    padding: 0,
    flexDirection: "column",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderBottomWidth: hairline,
    borderBottomColor: "#000000",
  },
  headerLeft: {
    fontSize: 28,
    fontWeight: "bold",
    letterSpacing: 1,
  },
  logo: {
    width: 118,
    height: 30,
    objectFit: "contain",
  },
  barcodeBand: {
    paddingHorizontal: 10,
    paddingTop: 8,
    paddingBottom: 6,
    alignItems: "center",
    borderBottomWidth: hairline,
    borderBottomColor: "#000000",
  },
  barcode: {
    width: "92%",
    height: 54,
  },
  barcodeText: {
    marginTop: 4,
    fontSize: 11,
    fontWeight: "bold",
    letterSpacing: 0.5,
  },
  housebill: {
    paddingHorizontal: 10,
    paddingTop: 6,
    paddingBottom: 8,
    borderBottomWidth: hairline,
    borderBottomColor: "#000000",
    alignItems: "center",
  },
  housebillLabel: {
    alignSelf: "flex-start",
    fontSize: 8,
    marginBottom: 2,
  },
  housebillValue: {
    fontSize: 26,
    fontWeight: "bold",
    letterSpacing: 0.5,
    textAlign: "center",
  },
  grid: {
    flexDirection: "column",
    borderBottomWidth: hairline,
    borderBottomColor: "#000000",
  },
  gridRow: {
    flexDirection: "row",
    borderBottomWidth: hairline,
    borderBottomColor: "#000000",
  },
  gridRowLast: {
    flexDirection: "row",
  },
  cell: {
    width: "50%",
    paddingHorizontal: 10,
    paddingVertical: 7,
  },
  cellLeft: {
    borderRightWidth: hairline,
    borderRightColor: "#000000",
  },
  cellLabel: {
    fontSize: 8,
    marginBottom: 2,
  },
  cellValue: {
    fontSize: 22,
    fontWeight: "bold",
    lineHeight: 1.1,
  },
  cellValueSm: {
    fontSize: 16,
    fontWeight: "bold",
    lineHeight: 1.15,
  },
  additional: {
    flexGrow: 1,
    paddingHorizontal: 10,
    paddingTop: 8,
    paddingBottom: 10,
  },
  additionalLabel: {
    fontSize: 8,
    marginBottom: 4,
  },
  shipToName: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 2,
  },
  shipToLine: {
    fontSize: 11,
    marginBottom: 1,
  },
  shipToCity: {
    fontSize: 13,
    fontWeight: "bold",
    marginTop: 2,
  },
  instructionsBlock: {
    marginTop: 6,
    paddingTop: 5,
    borderTopWidth: hairline,
    borderTopColor: "#000000",
  },
  instructionsLabel: {
    fontSize: 8,
    marginBottom: 2,
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  instructionsText: {
    fontSize: 10,
    lineHeight: 1.25,
  },
  serviceLine: {
    marginTop: 8,
    fontSize: 10,
    fontWeight: "bold",
  },
});

function logoSrc(): string {
  if (typeof window === "undefined") return "/logo_black.png";
  const base = import.meta.env.BASE_URL || "/";
  return new URL("logo_black.png", `${window.location.origin}${base}`).href;
}

function buildBarcodeDataUrl(value: string): string | null {
  try {
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, value, {
      format: "CODE128",
      width: 1.6,
      height: 54,
      displayValue: false,
      margin: 0,
    });
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
}

function formatLabelDate(d = new Date()): string {
  const months = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];
  const dd = String(d.getDate()).padStart(2, "0");
  const mon = months[d.getMonth()];
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}${mon}${yy}`;
}

function shortDestination(city: string, country: string): string {
  const c = (country || "").trim().toUpperCase();
  if (c.length === 2) return c;
  const cityShort = (city || "").trim().toUpperCase().slice(0, 3);
  return cityShort || c || "—";
}

function OwnDeliveryLabelDocument({
  data,
  barcodeDataUrl,
}: {
  data: OwnDeliveryLabelData;
  barcodeDataUrl: string | null;
}) {
  const weightKg =
    data.weightGrams != null && data.weightGrams > 0
      ? `${(data.weightGrams / 1000).toFixed(0)} K`
      : "—";

  const displayName = data.toCompanyName || data.toName;
  const showContactName = Boolean(
    data.toCompanyName && data.toName && data.toName !== data.toCompanyName,
  );
  const destCode = shortDestination(data.toCity, data.toCountry);
  const pieces = `${data.palletIndex}`;
  const piecesTotal = String(data.palletTotal);

  return (
    <Document>
      <Page size={DYMO_4X6_PORTRAIT_PT} style={styles.page} wrap={false}>
        <View style={styles.header}>
          <PDFText style={styles.headerLeft}>
            {data.palletIndex}/{data.palletTotal}
          </PDFText>
          <Image src={logoSrc()} style={styles.logo} />
        </View>

        <View style={styles.barcodeBand}>
          {barcodeDataUrl ? (
            <Image src={barcodeDataUrl} style={styles.barcode} />
          ) : null}
          <PDFText style={styles.barcodeText}>{data.deliveryId}</PDFText>
        </View>

        <View style={styles.housebill}>
          <PDFText style={styles.housebillLabel}>Booking</PDFText>
          <PDFText style={styles.housebillValue}>
            {data.bookingReference}
          </PDFText>
        </View>

        <View style={styles.grid}>
          <View style={styles.gridRow}>
            <View style={[styles.cell, styles.cellLeft]}>
              <PDFText style={styles.cellLabel}>Destination</PDFText>
              <PDFText style={styles.cellValue}>{destCode}</PDFText>
            </View>
            <View style={styles.cell}>
              <PDFText style={styles.cellLabel}>Total Number of Pieces</PDFText>
              <PDFText style={styles.cellValue}>
                {pieces}/{piecesTotal}
              </PDFText>
            </View>
          </View>

          <View style={styles.gridRow}>
            <View style={[styles.cell, styles.cellLeft]}>
              <PDFText style={styles.cellLabel}>Date</PDFText>
              <PDFText style={styles.cellValue}>{formatLabelDate()}</PDFText>
            </View>
            <View style={styles.cell}>
              <PDFText style={styles.cellLabel}>Total Weight</PDFText>
              <PDFText style={styles.cellValue}>{weightKg}</PDFText>
            </View>
          </View>

          <View style={styles.gridRowLast}>
            <View style={[styles.cell, styles.cellLeft]}>
              <PDFText style={styles.cellLabel}>Origin</PDFText>
              <PDFText style={styles.cellValue}>NL</PDFText>
            </View>
            <View style={styles.cell}>
              <PDFText style={styles.cellLabel}>Destination</PDFText>
              <PDFText style={styles.cellValueSm}>
                {(data.toCity || "").toUpperCase()}
              </PDFText>
            </View>
          </View>
        </View>

        <View style={styles.additional}>
          <PDFText style={styles.additionalLabel}>Ship To</PDFText>
          <PDFText style={styles.shipToName}>{displayName}</PDFText>
          {showContactName ? (
            <PDFText style={styles.shipToLine}>{data.toName}</PDFText>
          ) : null}
          <PDFText style={styles.shipToLine}>
            {data.toStreet} {data.toHouseNumber}
          </PDFText>
          <PDFText style={styles.shipToCity}>
            {data.toPostalCode} {data.toCity}
          </PDFText>
          <PDFText style={styles.shipToLine}>{data.toCountry}</PDFText>
          {data.instructions?.trim() ? (
            <View style={styles.instructionsBlock}>
              <PDFText style={styles.instructionsLabel}>
                Special Instructions
              </PDFText>
              {data.instructions
                .trim()
                .split(/\r?\n/)
                .filter(Boolean)
                .map((line, i) => (
                  <PDFText key={i} style={styles.instructionsText}>
                    {line}
                  </PDFText>
                ))}
            </View>
          ) : null}
          <PDFText style={styles.serviceLine}>OWN DELIVERY — NO T&amp;T</PDFText>
        </View>
      </Page>
    </Document>
  );
}

export async function generateOwnDeliveryLabelPdf(
  data: OwnDeliveryLabelData,
): Promise<Blob> {
  const barcodeDataUrl = buildBarcodeDataUrl(data.deliveryId);
  return pdf(
    <OwnDeliveryLabelDocument data={data} barcodeDataUrl={barcodeDataUrl} />,
  ).toBlob();
}

export async function downloadOwnDeliveryLabel(
  data: OwnDeliveryLabelData,
): Promise<void> {
  const blob = await generateOwnDeliveryLabelPdf(data);
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Resolve EFF{id}{AIR|SEA} when not already stored on the shipment. */
export async function resolveOwnDeliveryBookingReference(
  bookingId: number,
  storedReference?: string | null,
): Promise<string> {
  if (storedReference && /EFF\d+[A-Z]+/i.test(storedReference)) {
    return storedReference;
  }

  try {
    const { supabase } = await import("../lib/supabase");
    const { data } = await supabase
      .from("bookings")
      .select("id, booking_transport_types!transport_type(abbreviation)")
      .eq("id", bookingId)
      .maybeSingle();

    const tt = data?.booking_transport_types as
      | { abbreviation?: string }
      | { abbreviation?: string }[]
      | null
      | undefined;
    const abbrev = (
      Array.isArray(tt) ? tt[0]?.abbreviation : tt?.abbreviation
    )
      ?.trim()
      .toUpperCase();

    return `EFF${bookingId}${abbrev || ""}`;
  } catch {
    return storedReference || `EFF${bookingId}`;
  }
}

/** Load special instructions from booking_delivery when not stored on shipment. */
export async function resolveOwnDeliveryInstructions(
  bookingId: number,
  storedInstructions?: string | null,
): Promise<string | null> {
  if (storedInstructions?.trim()) return storedInstructions.trim();

  try {
    const { supabase } = await import("../lib/supabase");
    const { data } = await supabase
      .from("booking_delivery")
      .select("instructions, special_handling_requirements")
      .eq("booking_id", bookingId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    const instructions = (data?.instructions || "").toString().trim();
    const special = (data?.special_handling_requirements || "")
      .toString()
      .trim();
    if (instructions && special && instructions !== special) {
      return `${instructions}\n${special}`;
    }
    return instructions || special || null;
  } catch {
    return null;
  }
}

export function ownDeliveryLabelDataFromShipment(shipment: {
  sendcloud_shipment_id: string;
  booking_id: number;
  tracking_number?: string | null;
  to_name: string;
  to_company_name?: string | null;
  to_street_name: string;
  to_house_number: string;
  to_postal_code: string;
  to_city: string;
  to_country: string;
  weight?: number | null;
  parcel_number?: string | null;
  sendcloud_data?: Record<string, unknown> | null;
}): OwnDeliveryLabelData {
  const ctx = shipment.sendcloud_data as
    | {
        own_delivery?: {
          pallet_index?: number;
          pallet_total?: number;
          delivery_id?: string;
          booking_reference?: string;
          instructions?: string | null;
          special_handling?: string | null;
        };
      }
    | null
    | undefined;

  const own = ctx?.own_delivery;
  let palletIndex = own?.pallet_index ?? 1;
  let palletTotal = own?.pallet_total ?? 1;

  if (shipment.parcel_number?.includes("/")) {
    const [a, b] = shipment.parcel_number.split("/");
    const parsedIndex = parseInt(a, 10);
    const parsedTotal = parseInt(b, 10);
    if (!Number.isNaN(parsedIndex)) palletIndex = parsedIndex;
    if (!Number.isNaN(parsedTotal)) palletTotal = parsedTotal;
  }

  const storedInstructions = [own?.instructions, own?.special_handling]
    .map((s) => (s || "").toString().trim())
    .filter(Boolean);
  const uniqueInstructions = [...new Set(storedInstructions)];

  return {
    deliveryId:
      own?.delivery_id ||
      shipment.tracking_number ||
      shipment.sendcloud_shipment_id,
    bookingId: shipment.booking_id,
    bookingReference:
      own?.booking_reference || `EFF${shipment.booking_id}`,
    palletIndex,
    palletTotal,
    toName: shipment.to_name,
    toCompanyName: shipment.to_company_name,
    toStreet: shipment.to_street_name,
    toHouseNumber: shipment.to_house_number,
    toPostalCode: shipment.to_postal_code,
    toCity: shipment.to_city,
    toCountry: shipment.to_country,
    weightGrams: shipment.weight,
    instructions: uniqueInstructions.length
      ? uniqueInstructions.join("\n")
      : null,
  };
}
